import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

// Configuration
const BATCH_DELAY_MS = 50;  // Wait 50ms to collect symbols before fetching
const REFRESH_INTERVAL_MS = 60000;  // Refresh every 60 seconds
const CACHE_TTL_MS = 15 * 60 * 1000;  // 15 minutes

// Context
const QuoteContext = createContext(null);

/**
 * QuoteProvider - Batches quote requests for efficiency.
 * 
 * Instead of N individual requests for N symbols, this provider:
 * 1. Collects all symbol requests within a 50ms window
 * 2. Makes ONE bulk request to /quotes/bulk
 * 3. Distributes results to subscribers
 */
export const QuoteProvider = ({ children }) => {
  const [quotes, setQuotes] = useState({});  // symbol -> quote data
  const [loading, setLoading] = useState({});  // symbol -> boolean
  const [errors, setErrors] = useState({});  // symbol -> boolean
  
  const pendingSymbols = useRef(new Set());
  const batchTimeoutRef = useRef(null);
  const subscribedSymbols = useRef(new Set());
  const refreshIntervalRef = useRef(null);
  const lastFetchRef = useRef(null);
  
  // Fetch quotes for a batch of symbols
  const fetchBulkQuotes = useCallback(async (symbols) => {
    if (!symbols || symbols.length === 0) return;
    
    const symbolList = Array.from(symbols);
    console.log(`[QuoteProvider] Fetching ${symbolList.length} quotes in batch`);
    
    // Set loading state for all symbols
    setLoading(prev => {
      const next = { ...prev };
      symbolList.forEach(s => { next[s] = true; });
      return next;
    });
    
    try {
      const response = await axios.post(`${API_BASE}/quotes/bulk`, {
        symbols: symbolList
      }, { timeout: 10000 });
      
      const data = response.data;
      const quotesMap = data.quotes || {};
      
      // Update quotes state
      setQuotes(prev => {
        const next = { ...prev };
        symbolList.forEach(symbol => {
          if (quotesMap[symbol]) {
            next[symbol] = {
              price: quotesMap[symbol].price,
              currency: quotesMap[symbol].currency || 'USD',
              change: quotesMap[symbol].change,
              changePercent: quotesMap[symbol].changePercent,
              updatedAt: data.asOf ? new Date(data.asOf) : new Date(),
              cacheAge: data.cacheAge || 0,
              cacheStatus: data.cacheStatus,
              // Include source and marketStatus for market-closed UI logic
              source: quotesMap[symbol].source,
              marketStatus: data.marketStatus
            };
          }
        });
        return next;
      });
      
      // Clear errors for fetched symbols
      setErrors(prev => {
        const next = { ...prev };
        symbolList.forEach(s => { next[s] = false; });
        return next;
      });
      
      lastFetchRef.current = Date.now();
      
    } catch (err) {
      console.error('[QuoteProvider] Bulk fetch error:', err);
      
      // Set errors for all symbols
      setErrors(prev => {
        const next = { ...prev };
        symbolList.forEach(s => { next[s] = true; });
        return next;
      });
    } finally {
      // Clear loading state
      setLoading(prev => {
        const next = { ...prev };
        symbolList.forEach(s => { next[s] = false; });
        return next;
      });
    }
  }, []);
  
  // Process pending batch
  const processBatch = useCallback(() => {
    if (pendingSymbols.current.size > 0) {
      const symbols = new Set(pendingSymbols.current);
      pendingSymbols.current.clear();
      fetchBulkQuotes(symbols);
    }
    batchTimeoutRef.current = null;
  }, [fetchBulkQuotes]);
  
  // Subscribe to a symbol's quote - called by LivePriceModule
  const subscribeToQuote = useCallback((symbol) => {
    if (!symbol) return;
    
    const upperSymbol = symbol.toUpperCase();
    subscribedSymbols.current.add(upperSymbol);
    
    // If we don't have this quote yet, add to pending batch
    if (!quotes[upperSymbol]) {
      pendingSymbols.current.add(upperSymbol);
      
      // Schedule batch processing
      if (!batchTimeoutRef.current) {
        batchTimeoutRef.current = setTimeout(processBatch, BATCH_DELAY_MS);
      }
    }
    
    return upperSymbol;
  }, [quotes, processBatch]);
  
  // Unsubscribe from a symbol (when component unmounts)
  const unsubscribeFromQuote = useCallback((symbol) => {
    if (!symbol) return;
    subscribedSymbols.current.delete(symbol.toUpperCase());
  }, []);
  
  // Force refresh all subscribed symbols
  const refreshAll = useCallback(() => {
    if (subscribedSymbols.current.size > 0) {
      fetchBulkQuotes(subscribedSymbols.current);
    }
  }, [fetchBulkQuotes]);
  
  // Set up refresh interval
  useEffect(() => {
    refreshIntervalRef.current = setInterval(() => {
      // Only refresh if tab is visible and we have subscribers
      if (document.visibilityState === 'visible' && subscribedSymbols.current.size > 0) {
        refreshAll();
      }
    }, REFRESH_INTERVAL_MS);
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [refreshAll]);
  
  // Handle visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Refresh if we haven't fetched recently
        const timeSinceLastFetch = Date.now() - (lastFetchRef.current || 0);
        if (timeSinceLastFetch > CACHE_TTL_MS / 2) {
          refreshAll();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshAll]);
  
  // Get quote data for a symbol
  const getQuote = useCallback((symbol) => {
    const upperSymbol = symbol?.toUpperCase();
    return {
      quote: quotes[upperSymbol] || null,
      loading: loading[upperSymbol] || false,
      error: errors[upperSymbol] || false
    };
  }, [quotes, loading, errors]);
  
  return (
    <QuoteContext.Provider value={{ 
      subscribeToQuote, 
      unsubscribeFromQuote, 
      getQuote,
      refreshAll
    }}>
      {children}
    </QuoteContext.Provider>
  );
};

// Hook for components to use quotes
export const useQuote = (symbol) => {
  const context = useContext(QuoteContext);
  
  // Fall back to direct fetch if not in provider
  const [fallbackQuote, setFallbackQuote] = useState(null);
  const [fallbackLoading, setFallbackLoading] = useState(true);
  const [fallbackError, setFallbackError] = useState(false);
  
  useEffect(() => {
    if (!context && symbol) {
      // Direct fetch fallback (legacy behavior)
      const fetchQuote = async () => {
        try {
          const response = await axios.get(`${API_BASE}/quote?symbol=${symbol}`);
          setFallbackQuote({
            price: response.data.price,
            currency: response.data.currency || 'USD',
            change: response.data.change,
            changePercent: response.data.changesPercentage,
            updatedAt: new Date(),
            source: response.data.source,
            marketStatus: response.data.marketStatus
          });
          setFallbackError(false);
        } catch (err) {
          setFallbackError(true);
        } finally {
          setFallbackLoading(false);
        }
      };
      fetchQuote();
    }
  }, [context, symbol]);
  
  useEffect(() => {
    if (context && symbol) {
      context.subscribeToQuote(symbol);
      return () => context.unsubscribeFromQuote(symbol);
    }
  }, [context, symbol]);
  
  if (!context) {
    return { quote: fallbackQuote, loading: fallbackLoading, error: fallbackError };
  }
  
  return context.getQuote(symbol);
};

export default QuoteContext;
